import { Box, Button, Grid, Typography, Card, CardContent, useTheme, useMediaQuery, LinearProgress, CircularProgress, Chip, Stack, Tooltip } from '@mui/material';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { League, LeagueGame, Player, NflGame, RosterPositionEnum } from '../../../sdk/model';
import { leagueGameApi } from '../../../adapters/APIExporter';
import { getTeamFromLeague, isOwnerOfTeam } from '../../util/TeamUtils';
import { usePlayerDataLoadFlat } from '../../util/PlayerDataUtil';
import { loadGameRosterFromQuickstats } from '../../util/GameUtil';
import { TeamAvatar } from '../../util/AvatarUtils';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Constants for game analysis
const COMPLETED_GAME_STATUSES = new Set(['COMPLETE', 'CLOSED']);
const INACTIVE_ROSTER_POSITIONS = new Set(['BN', 'IR']);

// Helper function to calculate team player statistics
function calculateTeamStats(
  team: any, 
  playerMap: Map<string | undefined, Player | undefined>, 
  nflGameMap: Map<string, any>
) {
  if (!team?.playerRoster) {
    return { playersInPlay: 0, playersRemaining: 0, totalActivePlayers: 0, completionPercentage: 0 };
  }

  const stats = Object.keys(team.playerRoster)
    .filter(rosterPos => !INACTIVE_ROSTER_POSITIONS.has(rosterPos))
    .flatMap(rosterPos => team.playerRoster[rosterPos])
    .reduce((acc, rosterSpot) => {
      if (!rosterSpot?.playerId) return acc;
      
      const playerInfo = playerMap.get(rosterSpot.playerId);
      const currentWeekStats = playerInfo?.quick_stats?.currentWeeksStats;
      const nflGame = currentWeekStats ? nflGameMap.get(currentWeekStats.gameId!) : undefined;
      
      acc.totalActivePlayers++;
      
      if (nflGame?.quarter) {
        acc.playersInPlay++;
      } else if (nflGame && !COMPLETED_GAME_STATUSES.has(nflGame.status!)) {
        acc.playersRemaining++;
      } else if (nflGame && COMPLETED_GAME_STATUSES.has(nflGame.status!)) {
        acc.playersCompleted++;
      }
      
      return acc;
    }, { playersInPlay: 0, playersRemaining: 0, playersCompleted: 0, totalActivePlayers: 0 });

  const completionPercentage = stats.totalActivePlayers > 0 
    ? (stats.playersCompleted / stats.totalActivePlayers) * 100 
    : 0;

  return { ...stats, completionPercentage };
}

// TypeScript interfaces for better type safety
interface LeagueMatchupViewProps {
  league: League;
}

interface GameSummaryProps {
  game: LeagueGame;
  league: League;
  userId: string;
  playerMap: Map<string | undefined, Player | undefined>;
  nflGameMap: Map<string, any>;
  isCurrentWeek: boolean;
}

interface ScoreInfoProps {
  game: LeagueGame;
  league: League;
  home: boolean;
}

interface GameStatusProps {
  game: LeagueGame;
  league: League;
  homeTeam: any;
  awayTeam: any;
  playerMap: Map<string | undefined, Player | undefined>;
  nflGameMap: Map<string, any>;
}

interface LeagueMatchupLinksProps {
  seasonYear: string | undefined;
  league: League;
  currentWeek: string | undefined;
}

function LeagueMatchupView({ league }: LeagueMatchupViewProps) {
  const { leagueId, week: paramWeek, season: paramSeason } = useParams<{
    leagueId: string;
    week?: string;
    season?: string;
  }>();

  const [games, setGames] = React.useState<LeagueGame[] | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(true);

  const user = useAppSelector((state) => state.user);
  const playerMap = usePlayerDataLoadFlat() as Map<string | undefined, Player | undefined>;
  const nflGames = useAppSelector((state) => state.nflGameDataLoad);
  
  // Use current season defaults if not provided in params
  const week = paramWeek || league.currentSeason?.currentWeek?.toString();
  const season = paramSeason || league.currentSeason?.seasonYear?.toString();

  // Check if it's current week for live data
  const isCurrentWeek = league.currentSeason?.currentWeek?.toString() === week &&
                        league.currentSeason?.seasonYear?.toString() === season;
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Memoize expensive calculations
  const nflGameMap = React.useMemo(
    () => new Map(nflGames.map(nflGame => [nflGame.id!, nflGame])) as Map<string, any>,
    [nflGames]
  );

  React.useEffect(() => {
    if (!leagueId || !week || !season) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    leagueGameApi
      .getGameByWeek(leagueId, Number(week), Number(season))
      .then(response => {
        const processedGames = response.data.map(game => 
          loadGameRosterFromQuickstats(league, game, playerMap, nflGameMap)
        );
        setGames(processedGames);
      })
      .catch(error => {
        console.error('Error loading games:', error);
        setGames([]);
      })
      .finally(() => setIsLoading(false));
  }, [leagueId, week, season, league, playerMap, nflGameMap]);

  if (!league || isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!games || games.length === 0) {
    return (
      <Box>
        <LeagueMatchupLinks seasonYear={season} currentWeek={week} league={league} />
        <Box textAlign="center" mt={3}>
          <Typography variant="h6" color="text.secondary">
            No games found for Week {week}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box display="flex" justifyContent="center">
        <Box sx={{ width: '100%', maxWidth: 1200 }}>
          <Box textAlign="center" mb={3}>
            <Typography 
              variant="h4" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
            >
              Week {week}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {season}
            </Typography>
          </Box>
          
          <LeagueMatchupLinks seasonYear={season} currentWeek={week} league={league} />
          
          <Box mt={3}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {games.map((game) => (
                <Grid 
                  key={game.id}
                  item 
                  xs={12} 
                  md={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/league/${leagueId}/matchup/id/${game.id}`)}
                >
                  <GameSummary 
                    game={game} 
                    league={league} 
                    userId={user?.id || ''} 
                    playerMap={playerMap}
                    nflGameMap={nflGameMap}
                    isCurrentWeek={isCurrentWeek}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Component to display team avatar with optional progress ring
const TeamAvatarWithProgress = React.memo(({ 
  team, 
  playerMap, 
  nflGameMap, 
  isCurrentWeek,
  ...avatarProps 
}: { 
  team: any; 
  playerMap: Map<string | undefined, Player | undefined>; 
  nflGameMap: Map<string, any>; 
  isCurrentWeek: boolean;
} & any) => {
  const stats = React.useMemo(
    () => calculateTeamStats(team, playerMap, nflGameMap),
    [team, playerMap, nflGameMap]
  );

  const showProgress = isCurrentWeek && stats.totalActivePlayers > 0;
  const tooltipTitle = showProgress ? 
    `Game Progress: ${stats.playersCompleted}/${stats.totalActivePlayers} players finished${stats.playersInPlay > 0 ? `, ${stats.playersInPlay} currently playing` : ''}${stats.playersRemaining > 0 ? `, ${stats.playersRemaining} yet to play` : ''}` 
    : '';

  if (!showProgress) {
    // Just return the regular avatar without progress
    return <TeamAvatar {...avatarProps} />;
  }

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <Box sx={{ 
        position: 'relative', 
        display: 'inline-flex',
        cursor: 'help',
        width: '48px',
        height: '48px',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Progress ring behind avatar */}
        <CircularProgress
          variant="determinate"
          value={stats.completionPercentage}
          size={48}
          thickness={3}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            color: stats.playersInPlay > 0 ? '#4CAF50' : '#2196F3',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
            zIndex: 0,
          }}
        />
        
        {/* Team Avatar on top - perfectly centered */}
        <Box sx={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          backgroundColor: 'background.paper',
          borderRadius: '50%',
          padding: '1px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <TeamAvatar {...avatarProps} />
        </Box>
      </Box>
    </Tooltip>
  );
});

// Component to show team status indicator (winner/leader/favorite)
const TeamStatusChip = React.memo(({ 
  team, 
  game, 
  isHome, 
  homeTeam, 
  awayTeam 
}: { 
  team: any; 
  game: LeagueGame; 
  isHome: boolean;
  homeTeam: any;
  awayTeam: any;
}) => {
  const status = React.useMemo(() => {
    const homeScore = game.homeTeamScore || 0;
    const awayScore = game.awayTeamScore || 0;
    const homeProjection = game.homeTeamProjection || 0;
    const awayProjection = game.awayTeamProjection || 0;

    // If game is final, check for winner
    if (game.finalScore) {
      if (homeScore > awayScore && isHome) {
        return { type: 'winner', label: 'Winner', color: 'warning', icon: EmojiEventsIcon };
      } else if (awayScore > homeScore && !isHome) {
        return { type: 'winner', label: 'Winner', color: 'warning', icon: EmojiEventsIcon };
      }
      return null; // Not the winner or tie
    }

    // For ongoing or future games, use projections to show favorite
    // Include current scores in the comparison if available
    const homeTotal = homeScore + homeProjection;
    const awayTotal = awayScore + awayProjection;
    
    if (homeTotal > awayTotal && isHome) {
      return { type: 'favored', label: 'Favored', color: 'info', icon: TrendingUpIcon };
    } else if (awayTotal > homeTotal && !isHome) {
      return { type: 'favored', label: 'Favored', color: 'info', icon: TrendingUpIcon };
    }

    return null; // Not favored or tied
  }, [game, isHome, team, homeTeam, awayTeam]);

  if (!status) return null;

  const IconComponent = status.icon;

  return (
    <Chip
      icon={<IconComponent sx={{ fontSize: '0.8rem !important' }} />}
      label={status.label}
      size="small"
      color={status.color as any}
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 'bold',
        '& .MuiChip-icon': { fontSize: '0.7rem' },
        mt: 0.5,
        minWidth: 'auto', // Ensure consistent width
        '& .MuiChip-label': {
          px: 1, // Consistent padding
        },
        ...(status.type === 'winner' && {
          background: 'linear-gradient(45deg, #FFD700, #FFA500)',
          color: '#000',
          '& .MuiChip-icon': {
            color: '#000',
          }
        }),
      }}
    />
  );
});

const GameSummary = React.memo(({ game, league, userId, playerMap, nflGameMap, isCurrentWeek }: GameSummaryProps) => {
  const homeTeam = React.useMemo(
    () => getTeamFromLeague(league, game.homeTeamId),
    [league, game.homeTeamId]
  );
  const awayTeam = React.useMemo(
    () => getTeamFromLeague(league, game.awayTeamId),
    [league, game.awayTeamId]
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const isUserHomeTeam = React.useMemo(
    () => isOwnerOfTeam(homeTeam, userId),
    [homeTeam, userId]
  );
  const isUserAwayTeam = React.useMemo(
    () => isOwnerOfTeam(awayTeam, userId),
    [awayTeam, userId]
  );

  return (
    <Card 
      sx={{ 
        height: '100%',
        position: 'relative',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
        border: (isUserHomeTeam || isUserAwayTeam) ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 3 }, overflow: 'hidden' }}>
        {isMobile ? (
          // Mobile Layout - Stack vertically for better space usage
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Teams Row - Horizontal on mobile with fixed constraints */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              {/* Away Team - Fixed width to prevent overflow */}
              <Box sx={{ 
                width: '30%', 
                minWidth: '80px', 
                maxWidth: '110px',
                textAlign: 'center',
                overflow: 'hidden'
              }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block"
                  sx={{ 
                    mb: 0.5, 
                    fontSize: '0.6rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.1
                  }}
                >
                  {awayTeam?.owners?.[0]?.username ? `@${awayTeam?.owners?.[0]?.username}` : 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                  <TeamAvatarWithProgress 
                    {...awayTeam} 
                    team={awayTeam}
                    playerMap={playerMap}
                    nflGameMap={nflGameMap}
                    isCurrentWeek={isCurrentWeek}
                  />
                </Box>
                <Typography 
                  variant="body2" 
                  fontWeight="medium"
                  title={awayTeam?.name || 'Unknown'}
                  sx={{ 
                    color: isUserAwayTeam ? 'primary.main' : 'text.primary',
                    fontSize: '0.7rem',
                    lineHeight: 1.1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%'
                  }}
                >
                  {awayTeam?.name || 'Unknown'}
                </Typography>
                <Box sx={{ mt: 0.25, minHeight: '18px', display: 'flex', justifyContent: 'center' }}>
                  <TeamStatusChip 
                    team={awayTeam} 
                    game={game} 
                    isHome={false} 
                    homeTeam={homeTeam} 
                    awayTeam={awayTeam} 
                  />
                </Box>
              </Box>

              {/* VS and Scores - Fixed center section */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                width: '40%',
                minWidth: '100px',
                maxWidth: '130px',
                px: 0.5
              }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.25} sx={{ mb: 0.5 }}>
                  <ScoreInfo game={game} league={league} home={false} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mx: 0.25 }}>
                    VS
                  </Typography>
                  <ScoreInfo game={game} league={league} home={true} />
                </Box>
                {game.finalScore && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>
                    Final
                  </Typography>
                )}
              </Box>

              {/* Home Team - Fixed width to prevent overflow */}
              <Box sx={{ 
                width: '30%', 
                minWidth: '80px', 
                maxWidth: '110px',
                textAlign: 'center',
                overflow: 'hidden'
              }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block"
                  sx={{ 
                    mb: 0.5,
                    fontSize: '0.6rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.1
                  }}
                >
                  {homeTeam?.owners?.[0]?.username ? `@${homeTeam?.owners?.[0]?.username}` : 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                  <TeamAvatarWithProgress 
                    {...homeTeam} 
                    team={homeTeam}
                    playerMap={playerMap}
                    nflGameMap={nflGameMap}
                    isCurrentWeek={isCurrentWeek}
                  />
                </Box>
                <Typography 
                  variant="body2" 
                  fontWeight="medium"
                  title={homeTeam?.name || 'Unknown'}
                  sx={{ 
                    color: isUserHomeTeam ? 'primary.main' : 'text.primary',
                    fontSize: '0.7rem',
                    lineHeight: 1.1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%'
                  }}
                >
                  {homeTeam?.name || 'Unknown'}
                </Typography>
                <Box sx={{ mt: 0.25, minHeight: '18px', display: 'flex', justifyContent: 'center' }}>
                  <TeamStatusChip 
                    team={homeTeam} 
                    game={game} 
                    isHome={true} 
                    homeTeam={homeTeam} 
                    awayTeam={awayTeam} 
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          // Desktop Layout - Keep original Grid layout
          <Grid container spacing={2} alignItems="center">
            {/* Away Team */}
            <Grid item xs={5} sm={4}>
              <Box textAlign="center">
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  {awayTeam?.owners?.[0]?.username ? `@${awayTeam?.owners?.[0]?.username}` : 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                  <TeamAvatarWithProgress 
                    {...awayTeam} 
                    team={awayTeam}
                    playerMap={playerMap}
                    nflGameMap={nflGameMap}
                    isCurrentWeek={isCurrentWeek}
                  />
                </Box>
                <Box textAlign="center">
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ 
                      color: isUserAwayTeam ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {awayTeam?.name || 'Unknown'}
                  </Typography>
                  <Box sx={{ mt: 0.5, minHeight: '24px', display: 'flex', justifyContent: 'center' }}>
                    <TeamStatusChip 
                      team={awayTeam} 
                      game={game} 
                      isHome={false} 
                      homeTeam={homeTeam} 
                      awayTeam={awayTeam} 
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Score Section */}
            <Grid item xs={2} sm={4}>
              <Box textAlign="center">
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ mb: 1 }}>
                  <ScoreInfo game={game} league={league} home={false} />
                  <Typography variant="body2" color="text.secondary">
                    VS
                  </Typography>
                  <ScoreInfo game={game} league={league} home={true} />
                </Box>
                {game.finalScore && (
                  <Typography variant="caption" color="text.secondary">
                    Final
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Home Team */}
            <Grid item xs={5} sm={4}>
              <Box textAlign="center">
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  {homeTeam?.owners?.[0]?.username ? `@${homeTeam?.owners?.[0]?.username}` : 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                  <TeamAvatarWithProgress 
                    {...homeTeam} 
                    team={homeTeam}
                    playerMap={playerMap}
                    nflGameMap={nflGameMap}
                    isCurrentWeek={isCurrentWeek}
                  />
                </Box>
                <Box textAlign="center">
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ 
                      color: isUserHomeTeam ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {homeTeam?.name || 'Unknown'}
                  </Typography>
                  <Box sx={{ mt: 0.5, minHeight: '24px', display: 'flex', justifyContent: 'center' }}>
                    <TeamStatusChip 
                      team={homeTeam} 
                      game={game} 
                      isHome={true} 
                      homeTeam={homeTeam} 
                      awayTeam={awayTeam} 
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
});

const ScoreInfo = React.memo(({ game, league, home }: ScoreInfoProps) => {
  const score = home ? game.homeTeamScore : game.awayTeamScore;
  const projectedScore = home ? game.homeTeamProjection : game.awayTeamProjection;
  
  const { winner, isWinner, isLoser } = React.useMemo(() => {
    const gameWinner = (game?.homeTeamScore ?? 0) > (game?.awayTeamScore ?? 0) ? game.homeTeamId : game.awayTeamId;
    return {
      winner: gameWinner,
      isWinner: game.finalScore && gameWinner === (home ? game.homeTeamId : game.awayTeamId),
      isLoser: game.finalScore && gameWinner !== (home ? game.homeTeamId : game.awayTeamId),
    };
  }, [game.homeTeamScore, game.awayTeamScore, game.finalScore, game.homeTeamId, game.awayTeamId, home]);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: { xs: 0.5, sm: 1 },
        borderRadius: 1,
        bgcolor: isWinner 
          ? theme.palette.success.main + '20' 
          : isLoser 
            ? theme.palette.error.main + '20' 
            : 'transparent',
        border: isWinner 
          ? `1px solid ${theme.palette.success.main}` 
          : isLoser 
            ? `1px solid ${theme.palette.error.main}` 
            : '1px solid transparent',
        minWidth: { xs: '32px', sm: '60px' },
        maxWidth: { xs: '45px', sm: 'none' },
      }}
    >
      <Typography 
        variant="h6" 
        fontWeight="bold"
        sx={{
          color: isWinner 
            ? 'success.main' 
            : isLoser 
              ? 'error.main' 
              : 'text.primary',
          fontSize: { xs: '0.8rem', sm: '1.25rem' },
          lineHeight: 1.1,
        }}
      >
        {score ?? 0}
      </Typography>
      {projectedScore !== undefined && (
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.55rem', sm: '0.75rem' },
            lineHeight: 1,
            mt: 0.25
          }}
        >
          ({projectedScore.toFixed(1)})
        </Typography>
      )}
    </Box>
  );
});

const LeagueMatchupLinks = React.memo(({ seasonYear, league, currentWeek }: LeagueMatchupLinksProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const maxWeek = React.useMemo(
    () => Number(seasonYear) === league.currentSeason?.seasonYear 
      ? league.currentSeason?.currentWeek 
      : 17,
    [seasonYear, league.currentSeason?.seasonYear, league.currentSeason?.currentWeek]
  );

  const handleWeekClick = React.useCallback((weekNum: number) => {
    if (currentWeek === weekNum.toString()) return;
    navigate(`/league/${league.id}/week/${weekNum}/${seasonYear}`);
  }, [currentWeek, navigate, league.id, seasonYear]);

  const weekButtons = React.useMemo(() => {
    return Array.from({ length: (maxWeek ?? 17) }, (_, i) => {
      const weekNum = i + 1;
      const isCurrentWeek = currentWeek === weekNum.toString();
      
      return (
        <Button
          key={weekNum}
          variant={isCurrentWeek ? 'contained' : 'outlined'}
          disabled={isCurrentWeek}
          onClick={() => handleWeekClick(weekNum)}
          sx={{
            minWidth: { xs: '32px', sm: '40px' },
            height: { xs: '32px', sm: '40px' },
            p: { xs: '4px', sm: '8px' },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 'bold',
            '&:hover': {
              transform: 'scale(1.05)',
              transition: 'transform 0.1s ease-in-out',
            },
          }}
        >
          {weekNum}
        </Button>
      );
    });
  }, [maxWeek, currentWeek, handleWeekClick]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: { xs: 0.5, sm: 1 },
        p: { xs: 1, sm: 2 },
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflowX: 'auto',
      }}
    >
      {weekButtons}
    </Box>
  );
});

export default LeagueMatchupView;
